import { CSSProperties, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { AutoComplete } from "antd";
import { debounce } from "lodash";
import {getLoquateToken, searchAddress} from "services/RequestDataService";
import { AddressData, QuoteAddress } from "utilities/AddressUtility";

interface AddressSearchProps {
  allowClear?: boolean;
  value?: QuoteAddress;
  style?: CSSProperties
  className?: string
  onSelect: (selectedAddress: QuoteAddress) => void;
  onError: (error: any) => void;
}

export interface AddressSearchRef {
  triggerValidation : (address: QuoteAddress | undefined, needOpenList: boolean) => boolean;
}

const AddressSearch = forwardRef<AddressSearchRef, AddressSearchProps>((props: AddressSearchProps, ref) => {
  const {
    allowClear,
    style,
    className,
    value: selectedAddress,
    onError,
    onSelect,
  } = props;

  // seems like addressState is getting the value from form_item. Might not even need to pass the default value.
  const [unstructuredAddress, setUnstructuredAddress] = useState(selectedAddress?.unstructuredAddress);
  const tokenRef = useRef<string | undefined>();
  const [addressInputOpen, setAddressInputOpen] = useState(false);
  const [addressList, setAddressList] = useState<AddressData[]>([]);

  useImperativeHandle(ref, () => ({
    triggerValidation: (address: QuoteAddress | undefined, needOpenList: boolean) => {
      if(address && address.unstructuredAddress === unstructuredAddress){
        return true;
      }
      if(unstructuredAddress && needOpenList){
        fetchAddress(unstructuredAddress)
        setAddressInputOpen(true);
      }
        return false;
      },
  }))

  useEffect(() => {
    const setLoquate = async () => {
      tokenRef.current = await getLoquateToken()
    }
    if(!tokenRef.current){
      setLoquate()
    }
  }, []);

  useEffect(() => {
    setUnstructuredAddress(selectedAddress?.unstructuredAddress);
  }, [selectedAddress?.unstructuredAddress]);

 const fetchAddress = useCallback(async (addressSearch: string) => {
      try {
        if (addressSearch.trim().length < 3) {
          setAddressList([]);
          return;
        }

        if(!tokenRef.current){
          return
        }

        const data = await searchAddress(addressSearch, tokenRef.current);

        const addresses: AddressData[] = data.map((a: any) => ({
          data: a,
          label: a.fullAddress,
          value: a.fullAddress,
        }));

        setAddressList(addresses);
      } catch (error) {
        onError(error);
      }
    },[onError])

  const fetchAddressDebounce = useMemo(()=>
    debounce(fetchAddress, 300),[fetchAddress]);

  return (
    <AutoComplete
      id="searchInput"
      allowClear={allowClear}
      open={addressInputOpen}
      options={addressList}
      placeholder="Enter address..."
      style= {{ ...style }}
      className={className}
      value={unstructuredAddress}
      onFocus={() => setAddressInputOpen(true)}
      onBlur={() => setAddressInputOpen(false)}
      onChange={(value: string) => {
        const address = value?.toUpperCase();
        fetchAddressDebounce(address);
        setUnstructuredAddress(address);
      }}
      onSelect={(_: string, option: any) => {
        setAddressInputOpen(false);
        onSelect(option.data);
      }}
    />
  );
});

export default AddressSearch;
